import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, CircularProgress, InputAdornment, styled, TextField } from '@mui/material';

import useSettings from '../../hooks/useSettings';
import Icon from '../../Icon';

import useModuleSearch from '../../fields/components/useModuleSearch';
import { ModuleSearchPopupIndicator } from '../../fields/components/ModuleSearchComponents';
// eslint-disable-next-line import/no-cycle
import { showModuleSearchFieldDialog } from '../../fields/components/ModuleSearchFieldDialog';

interface MaterialModuleSearchProps {
	name: string;
	module: string;
	uid?: string;
	sendChange: (value: string | null) => void;
	disabled: boolean;
}

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	'& > .MuiTextField-root > .MuiInputBase-root': {
		width: 'auto',
		// padding: 6,
		'& > .MuiInputBase-input': {
			paddingTop: 2,
			// paddingRight: 16,
			paddingBottom: 2,
			paddingLeft: 0
		}
	}
}));

/** A SearchSelectField specific to modules */
function MaterialModuleSearch({ name, module, uid, sendChange, disabled }: MaterialModuleSearchProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(module);

	const isInsideAnotherDialog = useSelector(({ fuse }: any) => fuse.moduleSearchDialog.state);

	const [value, setValue] = React.useState();

	const {
		options,
		isLoading,
		PaperComponent,
		isOptionEqualToValue,
		parseOptionLabel,
		renderOption,
		handleInputChange,
		handleChange,
		handleKeyDown
	} = useModuleSearch({ module, uid, value, setValue, sendChange });

	const handleOpenDialog = () =>
		dispatch(
			showModuleSearchFieldDialog({
				label: '',
				module: module,
				filters: [],
				value: value,
				sendChange: handleChange,
				parseOptionLabel
			})
		);

	const startAdornment = (
		<InputAdornment position="start">
			{isLoading ? (
				<CircularProgress size={20} color="inherit" disableShrink />
			) : (
				<Icon icon={settings.modu_icon} size={20} color={value ? 'action.active' : 'text.disabled'} />
			)}
		</InputAdornment>
	);

	const PopupIndicator = React.forwardRef(() => ModuleSearchPopupIndicator({ handleOpenDialog }));

	return (
		<StyledAutocomplete
			renderInput={params => (
				<TextField
					{...params}
					name={name}
					variant="outlined"
					size="small"
					fullWidth
					autoComplete="off"
					InputLabelProps={{
						...params.InputLabelProps,
						shrink: true
					}}
					InputProps={{
						...params.InputProps,
						startAdornment
					}}
				/>
			)}
			slotProps={{
				paper: { component: PaperComponent },
				popupIndicator: { component: PopupIndicator }
			}}
			options={options}
			value={value || null}
			onInputChange={handleInputChange}
			onChange={(_event, newValue) => handleChange(newValue)}
			onKeyDown={handleKeyDown}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			filterOptions={() => options}
			disabled={disabled}
			isOptionEqualToValue={isOptionEqualToValue}
			getOptionLabel={parseOptionLabel}
			renderOption={renderOption}
			selectOnFocus
			forcePopupIcon={!isInsideAnotherDialog}
			// freeSolo
		/>
	);
}

export default React.memo(MaterialModuleSearch);
